import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Printer, ArrowDownLeft, ArrowUpRight, BookOpen, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatCard } from "@/components/Ui";
import { useAuth, userRoles } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

export default function CashLedger() {
  const { user } = useAuth();
  const roles = userRoles(user);
  const privileged = roles.includes("manager") || roles.includes("admin");
  const smOnly = roles.includes("station_master") && !privileged;
  const [kind, setKind] = useState(smOnly ? "station" : "cashier");
  const [target, setTarget] = useState("all");
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [opts, setOpts] = useState({ cashiers: [], stations: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (privileged) api.get("/cash/ledger/options").then((r) => setOpts(r.data)).catch(() => {});
  }, [privileged]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cash/ledger", { params: { kind, target, from_date: from, to_date: to } });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [kind, target, from, to]);
  useEffect(() => { load(); }, [load]);

  const ledgerTitle = kind === "station" ? "Station buffer ledger" : "Cashier ledger";

  const verify = async (r) => {
    try {
      const { data } = await api.post(`/cash/deposits/${r.ref_id}/verify`);
      toast.success(data.verified ? "Bank deposit verified" : "Verification removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const printTxn = (r) => {
    const w = window.open("", "_blank", "width=420,height=640");
    if (!w) { toast.error("Allow pop-ups to print the transaction"); return; }
    const amount = r.in ? money(r.in) : money(r.out);
    const dir = r.in ? "Cash in" : "Cash out";
    w.document.write(`<html><head><title>Transaction voucher</title><style>
      body{font-family:'DM Sans',Arial,sans-serif;padding:24px;color:#0f172a}
      h1{font-size:18px;margin:0 0 2px}.sub{color:#64748b;font-size:12px;margin-bottom:16px}
      .amt{font-size:28px;font-weight:800;margin:12px 0;color:#0052FF}
      table{width:100%;border-collapse:collapse;font-size:13px}
      td{padding:7px 0;border-bottom:1px solid #e2e8f0}td:last-child{text-align:right;font-weight:600}
      </style></head><body>
      <h1>GPRTU &middot; Nacasky Co. Ltd</h1>
      <div class="sub">${ledgerTitle} &middot; transaction voucher</div>
      <div class="amt">${amount}</div>
      <table>
        <tr><td>Type</td><td>${dir}</td></tr>
        <tr><td>Description</td><td>${r.label || ""}</td></tr>
        ${r.ref ? `<tr><td>Reference</td><td>${r.ref}</td></tr>` : ""}
        <tr><td>Date</td><td>${fmtDateTime(r.date)}</td></tr>
        <tr><td>Running balance</td><td>${money(r.balance)}</td></tr>
        ${r.type === "deposit" ? `<tr><td>Status</td><td>${r.verified ? "Verified" : "Pending verification"}</td></tr>` : ""}
      </table>
      <p class="sub" style="margin-top:24px">Printed ${new Date().toLocaleString()}</p>
      <script>window.onload=function(){window.print();}<\/script>
      </body></html>`);
    w.document.close();
  };

  const targetList = kind === "station"
    ? opts.stations.map((s) => ({ value: s.id, label: `${s.city} - ${s.name}` }))
    : opts.cashiers.map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="space-y-5" data-testid="cash-ledger">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold tracking-tight">
          <BookOpen className="h-6 w-6 text-[#0052FF]" />{kind === "station" ? "Station buffer ledger" : "Cashier ledger"}
        </h2>
        <div className="flex flex-wrap items-end gap-2">
          {privileged && (
            <>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Ledger</Label>
                <Select value={kind} onValueChange={(v) => { setKind(v); setTarget("all"); }}>
                  <SelectTrigger className="w-36" data-testid="ledger-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cashier">Cashier</SelectItem><SelectItem value="station">Station</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">{kind === "station" ? "Station" : "Cashier"}</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="w-52" data-testid="ledger-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {kind === "station" ? "stations" : "cashiers"}</SelectItem>
                    {targetList.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div><Label className="text-xs font-semibold text-slate-600">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="ledger-from-input" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="ledger-to-input" /></div>
          <Button variant="outline" className="rounded-xl" onClick={() => window.print()} data-testid="ledger-print-button"><Printer className="h-4 w-4" />Print</Button>
        </div>
      </div>
      {loading || !data ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Opening balance" value={money(data.opening)} tint="bg-slate-100 text-slate-600" testid="ledger-opening" />
            <StatCard label="Total in" value={money(data.total_in)} icon={ArrowDownLeft} tint="bg-emerald-50 text-emerald-600" testid="ledger-total-in" />
            <StatCard label="Total out" value={money(data.total_out)} icon={ArrowUpRight} tint="bg-rose-50 text-rose-600" testid="ledger-total-out" />
            <StatCard label="Closing balance" value={money(data.closing)} tint="bg-sky-50 text-sky-600" testid="ledger-closing" />
          </div>
          <div className="card-soft overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="p-3">Date</th><th className="p-3">Description</th>
                  <th className="p-3 text-right">In</th><th className="p-3 text-right">Out</th><th className="p-3 text-right">Balance</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100 bg-slate-50/60 font-semibold" data-testid="ledger-opening-row">
                  <td className="p-3" colSpan={4}>Opening balance</td><td className="p-3 text-right">{money(data.opening)}</td><td className="p-3"></td>
                </tr>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`ledger-row-${i}`}>
                    <td className="whitespace-nowrap p-3 text-slate-500">{fmtDateTime(r.date)}</td>
                    <td className="p-3">
                      {r.label}{r.ref ? <span className="text-xs text-slate-400"> · {r.ref}</span> : null}
                      {r.type === "deposit" && (
                        <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${r.verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`ledger-deposit-status-${i}`}>
                          {r.verified ? "Verified" : "Pending"}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right font-medium text-emerald-600">{r.in ? money(r.in) : ""}</td>
                    <td className="p-3 text-right font-medium text-rose-600">{r.out ? money(r.out) : ""}</td>
                    <td className="p-3 text-right font-semibold">{money(r.balance)}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {r.type === "deposit" && privileged && (
                          <Button size="sm" variant={r.verified ? "outline" : "default"} className={`h-7 gap-1 rounded-lg px-2 text-xs ${r.verified ? "" : "bg-emerald-600 text-white hover:bg-emerald-700"}`} onClick={() => verify(r)} data-testid={`ledger-verify-${i}`}>
                            {r.verified ? <><RotateCcw className="h-3 w-3" />Unverify</> : <><CheckCircle2 className="h-3 w-3" />Verify</>}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 rounded-lg px-2" onClick={() => printTxn(r)} data-testid={`ledger-print-row-${i}`} title="Print this transaction"><Printer className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td className="p-6 text-center text-slate-500" colSpan={6}>No cash movements in this date range.</td></tr>}
                <tr className="bg-slate-50/60 font-bold" data-testid="ledger-closing-row">
                  <td className="p-3" colSpan={2}>Closing balance</td>
                  <td className="p-3 text-right text-emerald-600">{money(data.total_in)}</td>
                  <td className="p-3 text-right text-rose-600">{money(data.total_out)}</td>
                  <td className="p-3 text-right">{money(data.closing)}</td>
                  <td className="p-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
